import React, { SetStateAction, useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import {
    Box,
    Button,
    IconButton,
    LinearProgress,
    LinearProgressProps,
    Paper,
    Typography,
} from "@mui/material";
import variables from "../../../assets/styles/variables.module.scss";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/Delete";
import { DataGrid, GridColDef, GridOverlay } from "@mui/x-data-grid";
import SummaryCard from "../../../Shared-ui/SharedComponents/Cards/SummaryCard";
import OldAgent from "../../../Shared-ui/Types/OldAgent";
import MetaInstance, {
    MetaMultipartInstance,
} from "../../../Services/AxiosInstance/MetaInstance";
import {
    GetFileName,
    GetFileSize,
    SplitFileIntoChuncks,
} from "../../../Shared-ui/utils/File/FileHelpers";
import { v4 as uuidv4 } from "uuid";
import { useDispatch } from "react-redux";
import {
    FILE_DELETE_AGENT,
    FILE_DELETE_TEMPLATE,
    FILE_UPLOAD_AGENT,
    FILE_UPLOAD_TEMPLATE,
    DOWNLOAD_FILE_TEMPLATE,
    DOWNLOAD_FILE_AGENT,
} from "../../../Services/Endpoint";
import { Params, useParams } from "react-router-dom";
import {
    ShowError,
    ShowSuccess,
} from "../../../Shared-ui/Store/MessageReducer/MsgActions";
import Template from "../../../Shared-ui/Types/Template";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { url } from "inspector";
import {
    StartLoading,
    StopLoading,
} from "../../../Shared-ui/Store/LoadingReducer/LoaderActions";
import Confirm from "../../../Shared-ui/Types/Confirm";
import ConfirmPopup from "../../../Shared-ui/SharedComponents/ConfirmPopup";
import { AgentFile } from "../../../Shared-ui/Types/common";
import { formatDate } from "../../../Shared-ui/utils/Common/Common";
import TableCustomNoData from "../../../Shared-ui/TableCustomNoData";
import { ALLOWEDEXTENSION } from "../../../Constant/Setting";

interface MyComponentProps {
    knowledgeBase: OldAgent | Template | any;
    setknowledgeBase: React.Dispatch<SetStateAction<OldAgent | any>>;
    getknowledgeBase: any;
    updateknowledgeBase: any;
}

function LinearProgressWithLabel(
    props: LinearProgressProps & { value: number }
) {
    return (
        <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box sx={{ width: "100%", mr: 1 }}>
                <LinearProgress variant="determinate" {...props} />
            </Box>
            <Box sx={{ minWidth: 35 }}>
                <Typography
                    variant="body2"
                    sx={{ color: "text.secondary" }}
                >{`${Math.round(props.value)}%`}</Typography>
            </Box>
        </Box>
    );
}

const SourceFiles: React.FC<MyComponentProps> = (props) => {
    const [progress, setProgress] = useState<number>(0);
    const paginationModel = { page: 0, pageSize: 5 };
    const [userSelectedFiles, setUserSelectedFiles] = useState<File[]>([]);
    const [userSelectedFile, setUserSelectedFile] = useState<File | null>(null);
    const [files, setFiles] = useState([
        ...userSelectedFiles,
        ...(props.knowledgeBase?.files || []),
    ]);
    const [pagination, setPagination] = useState({ page: 0, pageSize: 3 }); // Current page index
    const [displayedRows, setDisplayedRows] = useState<any>([]); // Current page index

    const { agentId } = useParams<Params>();
    const { templateId } = useParams<Params>();
    const dispatch = useDispatch();
    const [confirmPopupData, setConfirmPopupData] = useState<Confirm>({
        show: false,
    });
    const [fileNames, setFileNames] = useState<string>("");

    const pathname = window.location.pathname;
    const basePath = pathname.split("/").slice(0, -1).join("/");

    useEffect(() => {
        setFiles([...(props.knowledgeBase?.files || [])]);
    }, [props.knowledgeBase]);

    const onDrop = useCallback((acceptedFiles: File[]) => {
        setUserSelectedFiles(acceptedFiles);
        // const allFiles = [...acceptedFiles, ...props.knowledgeBase.files]

        if (acceptedFiles.length > 0) {
            acceptedFiles.map((file) => { // Use forEach instead of map
                const maxSize = 25 * 1048576; // 25 MB
                const fileExtension = file.name.split('.').pop()?.toLowerCase() || "";

                if (file.size > maxSize) {
                    dispatch(ShowError(`${file.name} size is larger`));
                    return;
                }

                if (fileExtension && !ALLOWEDEXTENSION.includes(fileExtension)) {
                    dispatch(ShowError(`${file.name} not allowed`));
                    return;
                }

                // Only set files that pass all checks
                setUserSelectedFile(file);
            });


            UploadFiles(acceptedFiles);
        }
        else {
            dispatch(ShowError(`Unsupported file format`));
            return;
        }
    }, []);

    const uploadChunk = (
        chunk: any,
        file: File,
        index: number,
        totalChunks: number,
        fileId: string,
        progressCallback: any
    ) => {
        const formData = new FormData();
        formData.append("file", chunk);
        formData.append("chunkNumber", String(index));
        formData.append("totalChunks", String(totalChunks));
        formData.append("fileId", fileId);
        formData.append("fileName", file.name);

        // formData.forEach((value, key) => {
        //   if (value instanceof File) {
        //     console.log(`${key}: ${value.name} (File)`);
        //   } else {
        //     console.log(`${key}: ${value}`);
        //   }
        // });

        let url = "";
        let id = "";
        if (basePath === "/template" && templateId) {
            url = FILE_UPLOAD_TEMPLATE;
            id = templateId;
        }
        if (basePath === "/agent" && agentId) {
            url = FILE_UPLOAD_AGENT;
            id = agentId;
        }
        return MetaMultipartInstance.post(`${url}/${id}/upload-chunk`, formData, {
            onUploadProgress: (progressEvent) => {
                if (progressEvent.total) {
                    const progress = (progressEvent.loaded / progressEvent.total) * 100;
                    progressCallback(file.name, index, progress);
                }
            },
        });
    };

    const UploadFiles = async (files: File[]) => {
        const totalChunks = files.reduce((sum, file) => sum + Math.ceil(file.size / (5 * 1024 * 1024)), 0);
        let uploadedChunks = 0;
        const names = files.map((file) => file.name).join(", ");
        setFileNames(names);
        const updateProgress = (increment: number) => {
            uploadedChunks += increment;
            setProgress(Math.min(100, Math.round((uploadedChunks / totalChunks) * 100)));
        };

        const uploadPromises = files.map(async (file) => {
            const chunks = SplitFileIntoChuncks(file);
            const fileId = uuidv4();

            for (let i = 0; i < chunks.length; i++) {
                await uploadChunk(chunks[i], file, i + 1, chunks.length, fileId, () => updateProgress(1));
            }
        });

        await Promise.all(uploadPromises)
            .then(() => {
                dispatch(ShowSuccess("File upload done"));
                setFiles([...(props.knowledgeBase?.files || [])]);
                setUserSelectedFile(null);
                setProgress(100); // Ensure it's 100 when done
                props.getknowledgeBase();
            })
            .catch(() => {
                setUserSelectedFile(null);
                setProgress(0);
            });
    };
    const deleteConfirm = (row: AgentFile | File) => {
        setConfirmPopupData({
            show: true,
            setConfirmPopupData: setConfirmPopupData,
            title: "Confirm Delete",
            info: "Are you sure want to delete your data? This action cannot be undone.",
            varient: "delete",
            functionName: deleteFile,
            functionParam: row,
        });
    };

    const deleteFile = async (row: AgentFile | File) => {
        try {
            setConfirmPopupData((preVal) => ({ ...preVal, show: false }));
            dispatch(StartLoading());
            let url = "";
            let id = "";
            if (basePath === "/template" && templateId) {
                url = FILE_DELETE_TEMPLATE;
                id = templateId;
            }
            if (basePath === "/agent" && agentId) {
                url = FILE_DELETE_AGENT;
                id = agentId;
            }
            const data = await MetaInstance.delete(
                `${url}/${id}/file?filename=${row.name}`
            ).then((res) => res?.data?.detail);
            dispatch(ShowSuccess(data.response));
        } catch (err: any) {
            dispatch(ShowError(err?.response?.data?.detail?.response));
        } finally {
            dispatch(StopLoading());
            props.getknowledgeBase();

        }
    };

    const downloadFile = async (row: AgentFile | File) => {
        try {
            setConfirmPopupData((preVal) => ({ ...preVal, show: false }));
            dispatch(StartLoading());
            let url = "";
            let id = "";
            if (basePath === "/template" && templateId) {
                url = DOWNLOAD_FILE_TEMPLATE;
                id = templateId;
            }
            if (basePath === "/agent" && agentId) {
                url = DOWNLOAD_FILE_AGENT;
                id = agentId;
            }
            const response = await MetaInstance.get(
                `${url}/${id}/downloads?name=${row.name}`, { responseType: "blob" }
            )
            // Create a Blob from the response data (binary content)
            const blob = new Blob([response.data], { type: "application/octet-stream" });
            const urlDownload = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = urlDownload;
            link.setAttribute('download', row?.name || ""); // Set the file name for download
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(urlDownload);
            dispatch(ShowSuccess("download started"));
        } catch (err: any) {
            dispatch(ShowError(err?.response?.data?.detail?.response));
        } finally {
            dispatch(StopLoading());
        }
    }

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            "image/": [".jpg", ".jpeg", ".png"], // Restrict to images
            "application/pdf": [".pdf", ".txt"], // Allow PDFs
            "application/msword": [".doc"],
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
                [".docx"],
            "application/vnd.ms-excel": [".xls"],
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
                ".xlsx",
            ],
            "text/csv": [".csv"],
        },
        multiple: true, // Allow multiple files
    });

    const handlePaginationMetaChange = (paginationMeta: any) => {
        const { page, pageSize, rowCount } = paginationMeta;
        // console.log(`Page: ${page + 1}, Page Size: ${pageSize}, Total Rows: ${rowCount}`);
        setPagination({ page: page, pageSize: pageSize });
        // setPage(page); // Update page
        // setPageSize(pageSize); // Update page size
    };

    const columns: GridColDef[] = [
        //{ field: 'id', headerName: 'ID', width: 70 },
        { field: "name", headerName: "Name", width: 180 },
        // { field: "type", headerName: "Type", width: 80 },
        {
            field: "trainingStatus",
            headerName: "Training Status",
            width: 150,
            renderCell: (params) => (
                <span
                    style={{
                        color:
                            params.value == "Failed"
                                ? variables.mainRed
                                : variables.mainGreen,
                    }}
                >
                    {params.value}
                </span>
            ),
        },
        {
            field: "updatedOn",
            headerName: "Updated On",
            width: 185,
            renderCell: (params: any) => {
                if (!params.value) return "";
                return formatDate(params.value)
            },
        },
        { field: "updatedBy", headerName: "Updated By", width: 130 },
        {
            field: "actions",
            headerName: "Actions",
            width: 120,
            sortable: false,
            renderCell: (params) => (
                <div>
                    {/* View Icon */}
                    <IconButton
                        aria-label="view"
                        onClick={() => downloadFile(params.row)}
                    >
                        <VisibilityOutlinedIcon />
                    </IconButton>

                    {/* Delete Icon */}
                    <IconButton
                        aria-label="delete"
                        onClick={() => deleteConfirm(params.row)}
                    >
                        <DeleteOutlineIcon />
                    </IconButton>
                </div>
            ),
        },
    ];

    return (
        <Box
            sx={{
                width: "100%",
                display: "block",
                boxSizing: "border-box",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    ml: -2,
                    mr: -2,
                }}
            >
                <Box
                    sx={{
                        width: {
                            lg: "calc(100% - 300px)",
                            md: "calc(100% - 300px)",
                            sm: "100%",
                        },
                        flex: "0 0 auto",
                        boxSizing: "border-box",
                        minWidth: {
                            lg: "calc(100% - 300px)",
                            md: "calc(100% - 300px)",
                            sm: "100%",
                        },
                        pl: 2,
                        pr: 2,
                        maxWidth: "100%",
                    }}
                >
                    <Typography variant="h6" gutterBottom>
                        Upload files to train your data
                    </Typography>
                    <Box
                        {...getRootProps()}
                        sx={{
                            border: `1px dashed ${variables.borderColor}`,
                            borderRadius: 2,
                            padding: 4,
                            textAlign: "center",
                            cursor: "pointer",
                            backgroundColor: isDragActive ? "#e3f2fd" : variables.white,
                            transition: "background-color 0.2s ease",
                            height: "150px",
                            my: 3,
                        }}
                    >
                        <input {...getInputProps()} />
                        {isDragActive ? (
                            <Typography variant="body1" color="primary">
                                Drop the files here...
                            </Typography>
                        ) : (
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    gap: 1,
                                }}
                            >
                                <UploadFileIcon sx={{ color: variables.secondaryColor }} />
                                <Typography variant="body1">
                                    Drag and drop files here, or click to select files
                                </Typography>
                                <Typography variant="caption">
                                    PDF, DOCX, XLSX, CSV, JPEG, PNG or TXT (max. 25MB)
                                </Typography>
                            </Box>
                        )}
                        {/* <Button
            variant="contained"
            color="primary"
            sx={{ mt: 2 }}
            onClick={(e) => e.preventDefault()}
          >
            Upload Files
          </Button> */}
                    </Box>
                    <Box
                        sx={{
                            display: "flex",
                            gap: 2,
                            alignItems: "center",
                            px: 2,
                        }}
                    >
                        {userSelectedFile && (
                            <>
                                {(() => {
                                    const { filename, fullfilename } =
                                        GetFileName(userSelectedFile);
                                    const fileSize = GetFileSize(userSelectedFile);
                                    return (
                                        <>
                                            <UploadFileIcon
                                                sx={{ color: variables.secondaryColor }}
                                            />
                                            <Box
                                                sx={{
                                                    width: "100%",
                                                }}
                                            >
                                                <Typography variant="body2">{fileNames}</Typography>
                                                <Box sx={{ width: "100%" }}>
                                                    <Typography variant="caption">
                                                        {fileSize}
                                                    </Typography>
                                                    <LinearProgressWithLabel
                                                        value={progress}
                                                        sx={{
                                                            borderRadius: 5,
                                                            backgroundColor: variables.secondaryLightColor, // Track color
                                                            "& .MuiLinearProgress-bar": {
                                                                backgroundColor: variables.secondaryColor, // Progress bar color
                                                            },
                                                        }}
                                                    />
                                                </Box>
                                            </Box>
                                            {/* <DeleteIcon
                                                sx={{
                                                    marginLeft: "auto",
                                                    color: variables.secondaryColor,
                                                }}
                                            /> */}
                                        </>
                                    );
                                })()}
                            </>
                        )}
                    </Box>
                    <Box>
                        <Paper sx={{ height: 300, width: "100%", mt: 2 }} elevation={0}>
                            <DataGrid
                                slots={{
                                    noRowsOverlay: TableCustomNoData,
                                }}
                                rows={files} // Paginated data
                                columns={columns}
                                rowCount={files.length}
                                pageSizeOptions={[3]}
                                getRowId={(row) => row.name}
                                initialState={{
                                    pagination: {
                                        paginationModel: {
                                            page: pagination.page, // Initial page index (0-based)
                                            pageSize: pagination.pageSize, // Initial page size
                                        },
                                    },
                                }}
                                onPaginationModelChange={handlePaginationMetaChange} // Handle pagination meta change
                                paginationMode="client"
                                disableColumnResize // Disables resizing
                                sx={{
                                    border: 1,
                                    borderColor: variables.borderColor,
                                    "& .MuiDataGrid-cell:focus": {
                                        outline: "none",
                                    },
                                    "& .MuiDataGrid-cell:focus-within": {
                                        outline: "none",
                                    },
                                }}
                            />
                        </Paper>
                    </Box>
                </Box>
                <Box
                    sx={{
                        width: { lg: "300px", md: "300px", sm: "100%", xs: "100%" },
                        minWidth: { lg: "300px", md: "300px", sm: "100%", xs: "100%" },
                        boxSizing: "border-box",
                        flex: "0 0 auto",
                        pl: 2,
                        pr: 2,
                        mt: { lg: "0", md: "0", sm: "30px", xs: "30px" },
                    }}
                >
                    <SummaryCard />
                </Box>
            </Box>
            <ConfirmPopup details={confirmPopupData} />
        </Box>
    );
};

export default SourceFiles;
